import { QueryHookOptions, gql } from '@apollo/client'
import { formatReactions } from 'app/features/group/helpers'
import { usePersistedCacheQuery } from 'app/hooks'
import { GroupAnnouncement, GroupPost, ReactionType } from 'app/types'

const query = gql`
  query getPostById($id: String!) {
    groupPostById(id: $id) {
      post {
        type
        id
        createdAt
        editedAt
        text
        link
        totalComments

        # Announcement
        body
        scheduledAt

        creator {
          id
          fullName
          yearOfStudy
          avatar {
            id
            sizes {
              small {
                url
              }
              medium {
                url
              }
            }
          }
          university {
            id
            name
          }
          subjectOfStudy {
            id
            name
          }
        }
        group {
          id
          name
          profileImage {
            id
            sizes {
              medium {
                url
              }
              small {
                url
              }
            }
          }
          visibility
          university {
            slug
          }
          universityCourse {
            slug
          }
          allowedUserTypes
          isOwner
          isMember
        }
        images {
          id
          image {
            id
            width
            height
            url
          }
        }
        reactions {
          id
          reaction
          user {
            id
          }
        }
      }
    }
  }
`

export interface GetPostByIdQueryInput {
  id: string
}

export interface GetPostByIdQueryOutput {
  groupPostById: {
    post: {
      type: 'post' | 'announcement'
      id: string
      text: string
      link: string
      totalComments: number
      createdAt: string
      editedAt: string | null
      body?: string
      scheduledAt?: string
      creator: {
        id: string
        fullName: string
        yearOfStudy: string
        subjectOfStudy: {
          id: string
          name: string
        }
        avatar: {
          id: string
          sizes: {
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        university: {
          id: string
          name: string
        }
      }
      reactions: {
        id: string
        reaction: ReactionType
        user: {
          id: string
        }
      }[]
      group: {
        id: string
        name: string
        profileImage: {
          sizes: {
            id: string
            small: {
              url: string
            }
            medium: {
              url: string
            }
          }
        }
        visibility: string
        university?: {
          slug: string
        }
        universityCourse?: {
          slug: string
        }
        allowedUserTypes: string[]
        isOwner: boolean
        isMember: boolean
      }
      images: {
        id: string
        image: {
          id: string
          width: number
          height: number
          url: string
        }
      }[]
    }
  }
}

const mapFn = (
  data: GetPostByIdQueryOutput['groupPostById'] | undefined
): GroupPost | GroupAnnouncement | undefined => {
  if (!data?.post) {
    return undefined
  }

  const post = data.post
  const creator = post.creator
  const group = post.group
  const reactions = formatReactions(post.reactions)

  const postCreator = {
    id: creator.id,
    fullName: creator.fullName,
    yearOfStudy: creator.yearOfStudy,
    subjectOfStudy: {
      id: creator.subjectOfStudy?.id,
      name: creator.subjectOfStudy?.name,
    },
    university: {
      name: creator.university?.name || '',
      id: creator.university?.id || '',
    },
    avatar: {
      medium: creator.avatar?.sizes.medium.url,
      small: creator.avatar?.sizes.small.url,
    },
  }

  const postGroup = {
    id: group.id || '',
    name: group.name || '',
    href: `/groups/${group.id}`,
    profileImage: {
      small: group.profileImage?.sizes.small.url || '',
    },
    university: group.university,
    universityCourse: group.universityCourse,
    allowedUserTypes: group.allowedUserTypes,
    isOwner: group.isOwner,
    isMember: group.isMember,
  }

  if (post.type === 'announcement') {
    const announcement: GroupAnnouncement = {
      type: 'groupAnnouncement',
      id: post.id,
      title: post.text,
      body: post.body,
      scheduledDate: post.scheduledAt,
      href: `/groups/posts/${post.id}`,
      reactions,
      totalComments: post.totalComments || 0,
      createdAt: post.createdAt,
      editedAt: post.editedAt,
      creator: postCreator,
      group: postGroup,
    }
    return announcement
  }

  if (post.type === 'post') {
    const groupPost: GroupPost = {
      type: 'groupPost',
      id: post.id,
      text: post.text,
      href: `/groups/posts/${post.id}`,
      reactions,
      totalComments: post.totalComments || 0,
      createdAt: post.createdAt,
      editedAt: post.editedAt,
      creator: postCreator,
      group: postGroup,
      image: {
        url: post.images[0]?.image?.url || '',
        width: post.images[0]?.image?.width || 1,
        height: post.images[0]?.image?.height || 1,
      },
    }
    return groupPost
  }

  return undefined
}

export const useGroupPostByIdQuery = (
  input: GetPostByIdQueryInput,
  options?: QueryHookOptions<GetPostByIdQueryOutput, GetPostByIdQueryInput>
) =>
  usePersistedCacheQuery<GetPostByIdQueryOutput, GetPostByIdQueryInput>(query, {
    variables: input,
    ...options,
  })

export const groupPostByIdQuery = {
  query,
  mapFn,
}
